/** Sparse row replacements against a previously issued bundle. Audio bytes never travel here. */
export interface BundleDelta {
  delta: 1
  baseHash: string
  resultHash: string
  fields: Record<string, unknown>
  removed: string[]
  arrays: Record<string, { length: number; rows: Array<[number, unknown]> }>
}

/** 64 rows per hash keeps the upload small even on the largest courses. */
export const MANIFEST_CHUNK_ROWS = 64
export interface BundleManifest {
  baseHash: string
  keys: string[]
  arrays: Record<string, string[]>
}
export interface BundleManifestRequest { manifestRequired: true }

export function needsBundleManifest(value: unknown): value is BundleManifestRequest {
  return !!value && typeof value === 'object' && (value as BundleManifestRequest).manifestRequired === true
}

export async function bundleManifest(base: object): Promise<BundleManifest> {
  const arrays: Record<string, string[]> = {}
  await Promise.all(Object.entries(base).map(async ([key, value]) => {
    if (!Array.isArray(value)) return
    arrays[key] = await Promise.all(Array.from({ length: Math.ceil(value.length / MANIFEST_CHUNK_ROWS) }, (_, i) =>
      bundleHash(value.slice(i * MANIFEST_CHUNK_ROWS, (i + 1) * MANIFEST_CHUNK_ROWS))))
  }))
  return { baseHash: await bundleHash(base), keys: Object.keys(base), arrays }
}

export function validBundleManifest(value: unknown): value is BundleManifest {
  if (!value || typeof value !== 'object') return false
  const m = value as BundleManifest
  const hash = (s: unknown) => typeof s === 'string' && /^[a-f0-9]{64}$/.test(s)
  if (!hash(m.baseHash) || !Array.isArray(m.keys) || m.keys.length > 100 ||
      !m.keys.every((k) => typeof k === 'string' && k.length < 100) || !m.arrays || typeof m.arrays !== 'object') return false
  const entries = Object.entries(m.arrays)
  return entries.length <= 100 && entries.every(([k, a]) => k.length < 100 && Array.isArray(a) && a.length <= 4096 && a.every(hash)) &&
    entries.reduce((n, [, a]) => n + a.length, 0) <= 4096
}

/** When the old server copy has expired, reuse chunks whose hashes still match. */
export async function makeManifestDelta(manifest: BundleManifest, next: Record<string, unknown>): Promise<BundleDelta> {
  const fields: Record<string, unknown> = {}
  const arrays: BundleDelta['arrays'] = {}
  await Promise.all(Object.entries(next).map(async ([key, value]) => {
    if (!Array.isArray(value) || !Object.prototype.hasOwnProperty.call(manifest.arrays, key)) { fields[key] = value; return }
    const rows: Array<[number, unknown]> = []
    const chunks = await Promise.all(Array.from({ length: Math.ceil(value.length / MANIFEST_CHUNK_ROWS) }, async (_, i) => {
      const chunk = value.slice(i * MANIFEST_CHUNK_ROWS, (i + 1) * MANIFEST_CHUNK_ROWS)
      return await bundleHash(chunk) === manifest.arrays[key][i] ? [] : chunk.map((row, j) => [i * MANIFEST_CHUNK_ROWS + j, row] as [number, unknown])
    }))
    for (const chunk of chunks) rows.push(...chunk)
    // Always send the length: a deletion may leave every retained chunk equal.
    arrays[key] = { length: value.length, rows }
  }))
  return { delta: 1, baseHash: manifest.baseHash, resultHash: await bundleHash(next), fields, arrays,
    removed: manifest.keys.filter((k) => !Object.prototype.hasOwnProperty.call(next, k)) }
}

export async function bundleHash(value: unknown): Promise<string> {
  const json = JSON.stringify(value, (_key, v) => v && typeof v === 'object' && !Array.isArray(v)
    ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]])) : v)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(json))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function makeBundleDelta(base: Record<string, unknown>, next: Record<string, unknown>): Promise<BundleDelta> {
  const fields: Record<string, unknown> = {}
  const arrays: BundleDelta['arrays'] = {}
  for (const [key, value] of Object.entries(next)) {
    if (Array.isArray(value) && Array.isArray(base[key])) {
      const old = base[key] as unknown[]
      const rows: Array<[number, unknown]> = []
      value.forEach((row, i) => { if (JSON.stringify(row) !== JSON.stringify(old[i])) rows.push([i, row]) })
      if (rows.length || value.length !== old.length) arrays[key] = { length: value.length, rows }
    } else if (JSON.stringify(value) !== JSON.stringify(base[key])) fields[key] = value
  }
  return {
    delta: 1, baseHash: await bundleHash(base), resultHash: await bundleHash(next),
    fields, arrays, removed: Object.keys(base).filter((key) => !Object.prototype.hasOwnProperty.call(next, key)),
  }
}

export function isBundleDelta(value: unknown): value is BundleDelta {
  return !!value && typeof value === 'object' && (value as BundleDelta).delta === 1
}

/** Reject mismatched or damaged bases; the caller can ask for a complete bundle instead. */
export async function applyBundleDelta<T extends object>(base: T, patch: BundleDelta): Promise<T> {
  if (await bundleHash(base) !== patch.baseHash) throw new Error('Bundle delta base mismatch')
  const result: Record<string, unknown> = { ...base, ...patch.fields }
  for (const key of patch.removed) delete result[key]
  for (const [key, change] of Object.entries(patch.arrays)) {
    if (!Array.isArray(result[key])) throw new Error('Bundle delta array mismatch')
    const rows = (result[key] as unknown[]).slice(0, change.length)
    rows.length = change.length
    for (const [index, row] of change.rows) {
      if (!Number.isInteger(index) || index < 0 || index >= change.length) throw new Error('Bundle delta index mismatch')
      rows[index] = row
    }
    result[key] = rows
  }
  if (await bundleHash(result) !== patch.resultHash) throw new Error('Bundle delta result mismatch')
  return result as T
}
